/**
 * The `GET /time` handshake and the re-sync schedule (D9, docs/03 Appendix B).
 *
 * `lib/` imports nothing from `api/`, so the transport arrives as a function.
 * That keeps the whole sampling policy — serial, spread, the re-sample on a
 * failed floor — testable with a fake fetcher and a fake sleeper, which is the
 * only way to test the paths that only appear on a bad mobile link.
 */

import type { Clock, SyncResult } from './clock'
import { MIN_SAMPLES, type TimeSample } from './offset'

/** What `GET /time` returns, reduced to the two fields the estimate needs. */
export interface ServerTimeReading {
  server_time_ms: number
  /** Optional in the schema. Absent means the two server stamps coincide. */
  server_recv_ms?: number | undefined
}

export type TimeFetcher = (clientSentMs: number, signal?: AbortSignal) => Promise<ServerTimeReading>

export interface HandshakeOptions {
  /** §9.6: ten samples on cold load. Three on wake and on reconnect. */
  samples?: number
  /** Spread across roughly this long, so consecutive samples do not share a queue. */
  spreadMs?: number
  /** Spacing between samples. Overrides `spreadMs / samples` when given —
   *  a five-sample re-sync wants the same ~200ms spacing as a ten-sample cold
   *  load, not half the wall-clock time spread over half the requests. */
  sampleGapMs?: number
  /** Injected for tests. */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>
  wallNow?: () => number
  signal?: AbortSignal
  /** §9.6 step 3: one retry when fewer than three samples survive. */
  resampleOnce?: boolean
}

export const COLD_LOAD_SAMPLES = 10
/**
 * **Five, not three, and docs/04 §9.6's own arithmetic is why** (register §F29).
 *
 * Rule 1 discards any sample whose round trip exceeds twice the set's minimum;
 * rule 3 needs three survivors or the whole handshake re-samples. A three-sample
 * re-sync therefore fails its floor the moment *one* sample is an outlier, which
 * on a mobile network is most of them — so the cheap path was the one that
 * always took the expensive one. Five leaves room for two outliers and still
 * clears the floor. `docs/03` §9.2.8 says five; this was the one place that
 * disagreed.
 */
export const RESYNC_SAMPLES = 5
export const COLD_LOAD_SPREAD_MS = 2000

/**
 * Collect samples **serially**. Parallel requests share queueing delay and
 * correlate their error, which defeats the median: ten samples that are all
 * wrong the same way look exactly like ten good ones.
 */
export async function collectSamples(
  fetchTime: TimeFetcher,
  options: HandshakeOptions = {},
): Promise<TimeSample[]> {
  const count = options.samples ?? COLD_LOAD_SAMPLES
  const spreadMs = options.spreadMs ?? COLD_LOAD_SPREAD_MS
  const sleep = options.sleep ?? defaultSleep
  const wallNow = options.wallNow ?? (() => Date.now())
  const gapMs =
    count > 1 ? Math.max(0, Math.floor(options.sampleGapMs ?? spreadMs / COLD_LOAD_SAMPLES)) : 0

  const samples: TimeSample[] = []
  for (let i = 0; i < count; i++) {
    if (options.signal?.aborted) break
    const t0 = wallNow()
    try {
      const reading = await fetchTime(t0, options.signal)
      const t3 = wallNow()
      const t2 = reading.server_time_ms
      samples.push({ t0, t1: reading.server_recv_ms ?? t2, t2, t3 })
    } catch {
      // A dropped sample is a dropped sample. The filter and the sample floor
      // decide whether what is left is usable; a throw here would turn one
      // flaky request into no clock at all.
    }
    if (i < count - 1 && gapMs > 0) await sleep(gapMs, options.signal)
  }
  return samples
}

export interface HandshakeResult extends SyncResult {
  samples: TimeSample[]
  /** True when the sample floor forced a second round. */
  resampled: boolean
}

/** Run the handshake and apply it to `clock`. */
export async function syncClock(
  clock: Clock,
  fetchTime: TimeFetcher,
  options: HandshakeOptions = {},
): Promise<HandshakeResult> {
  let samples = await collectSamples(fetchTime, options)
  let resampled = false

  if ((options.resampleOnce ?? true) && samples.length < MIN_SAMPLES && !options.signal?.aborted) {
    resampled = true
    samples = [...samples, ...(await collectSamples(fetchTime, options))]
  }

  return { ...clock.applySync(samples), samples, resampled }
}

// ---------------------------------------------------------------------------
// The re-sync schedule
// ---------------------------------------------------------------------------

/**
 * Appendix B: clock re-sync every 15 minutes, plus on wake after >60s hidden,
 * plus on every reconnect.
 *
 * The interval is a parameter rather than a constant because docs/04 §9.6's
 * table says 60 seconds where Appendix B says 15 minutes. Appendix B is the
 * enforced timer list and the one the plan and lint rule are written against,
 * so it is the default here; the divergence is real and is worth a register
 * entry rather than a silent choice. Note that between full syncs the clock is
 * not uncorrected — `observeServerTime` runs on every frame and every payload,
 * which is what actually bounds drift, and is why a 60-second network re-sync
 * on top of it buys little.
 */
export const RESYNC_INTERVAL_MS = 15 * 60 * 1000
/** Hidden for longer than this and the wall clock may have jumped. */
export const WAKE_RESYNC_THRESHOLD_MS = 60 * 1000

export interface SchedulerHost {
  setTimer: (fn: () => void, ms: number) => number
  clearTimer: (handle: number) => void
}

export interface ClockSyncSchedulerOptions {
  clock: Clock
  fetchTime: TimeFetcher
  intervalMs?: number
  resyncSamples?: number
  host?: SchedulerHost
  /** Passed through to every handshake this scheduler runs — chiefly `sleep`,
   *  so a test does not spend real seconds between samples. */
  handshake?: Omit<HandshakeOptions, 'samples'>
  onSync?: (result: HandshakeResult) => void
  onError?: (error: unknown) => void
}

/**
 * Drives the periodic and event-triggered re-syncs. One timer, self-rescheduling
 * — the interval is never allowed to overlap its own run, which on a throttled
 * background tab is otherwise how a queue of `/time` requests builds up.
 */
export class ClockSyncScheduler {
  readonly #clock: Clock
  readonly #fetchTime: TimeFetcher
  readonly #intervalMs: number
  readonly #samples: number
  readonly #host: SchedulerHost
  readonly #handshake: Omit<HandshakeOptions, 'samples'>
  readonly #onSync: ((result: HandshakeResult) => void) | undefined
  readonly #onError: ((error: unknown) => void) | undefined

  #handle: number | null = null
  #running = false
  #inFlight: Promise<HandshakeResult> | null = null

  constructor(options: ClockSyncSchedulerOptions) {
    this.#clock = options.clock
    this.#fetchTime = options.fetchTime
    this.#intervalMs = options.intervalMs ?? RESYNC_INTERVAL_MS
    this.#samples = options.resyncSamples ?? RESYNC_SAMPLES
    this.#host = options.host ?? {
      setTimer: (fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number,
      clearTimer: (h) => globalThis.clearTimeout(h),
    }
    this.#handshake = options.handshake ?? {}
    this.#onSync = options.onSync
    this.#onError = options.onError
  }

  get running(): boolean {
    return this.#running
  }

  start(): void {
    if (this.#running) return
    this.#running = true
    this.#arm()
  }

  stop(): void {
    this.#running = false
    if (this.#handle !== null) {
      this.#host.clearTimer(this.#handle)
      this.#handle = null
    }
  }

  /** Re-sync now, restarting the interval. Concurrent calls share one run —
   *  a reconnect and a wake landing together must not fire two handshakes. */
  resync(reason: 'INTERVAL' | 'WAKE' | 'RECONNECT' | 'MANUAL' = 'MANUAL'): Promise<HandshakeResult> {
    void reason
    if (this.#inFlight) return this.#inFlight
    if (this.#handle !== null) {
      this.#host.clearTimer(this.#handle)
      this.#handle = null
    }

    const run = syncClock(this.#clock, this.#fetchTime, { ...this.#handshake, samples: this.#samples })
      .then((result) => {
        this.#onSync?.(result)
        return result
      })
      .catch((error: unknown) => {
        this.#onError?.(error)
        throw error
      })
      .finally(() => {
        this.#inFlight = null
        if (this.#running) this.#arm()
      })

    this.#inFlight = run
    return run
  }

  /** Call from a `visibilitychange → visible` handler. Re-syncs only when the
   *  tab was hidden long enough for the wall clock to have moved under it. */
  onVisible(hiddenForMs: number): Promise<HandshakeResult> | null {
    if (hiddenForMs < WAKE_RESYNC_THRESHOLD_MS) return null
    this.#clock.invalidate()
    return this.resync('WAKE')
  }

  onReconnect(): Promise<HandshakeResult> {
    return this.resync('RECONNECT')
  }

  #arm(): void {
    if (this.#handle !== null) this.#host.clearTimer(this.#handle)
    this.#handle = this.#host.setTimer(() => {
      this.#handle = null
      void this.resync('INTERVAL').catch(() => {})
    }, this.#intervalMs)
  }
}

function defaultSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const handle = globalThis.setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        globalThis.clearTimeout(handle)
        resolve()
      },
      { once: true },
    )
  })
}
